import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { TID } from '@/constants/testIds';
import { LogoutDialog } from '@/components/LogoutDialog';
import { Check, X } from 'lucide-react';

function checks(pwd) {
  return {
    length: pwd.length >= 10,
    lower: /[a-z]/.test(pwd),
    upper: /[A-Z]/.test(pwd),
    digit: /\d/.test(pwd),
    symbol: /[^A-Za-z0-9]/.test(pwd),
  };
}

function Rule({ ok, text }) {
  return (
    <div className={`flex items-center gap-1.5 text-xs ${ok ? 'text-emerald-700' : 'text-slate-500'}`}>
      {ok ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
      {text}
    </div>
  );
}

export default function ChangePassword() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);

  const rules = useMemo(() => checks(next), [next]);
  const allValid = rules.length && rules.lower && rules.upper && rules.digit && rules.symbol;
  const strength = Object.values(rules).filter(Boolean).length;

  const submit = async (e) => {
    e.preventDefault();
    if (!allValid) return toast.error('Le mot de passe ne respecte pas la politique de sécurité.');
    if (next !== confirm) return toast.error('Les mots de passe ne correspondent pas');
    if (next === current) return toast.error('Le nouveau mot de passe doit être différent de l\'ancien.');
    setLoading(true);
    try {
      await authApi.changePassword(current, next);
      toast.success('Mot de passe mis à jour');
      await refresh();
      if (user?.role === 'platform_owner' || user?.role === 'admin') navigate('/admin');
      else if (user?.role === 'agent') navigate('/agent');
      else if (user?.role === 'bailleur') navigate('/bailleur');
      else navigate('/locataire');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-8">
        <div className="mb-6">
          <div className="inline-flex items-center rounded-full bg-[#E6F1FF] px-3 py-1 text-xs font-semibold text-[#1677FF] mb-3">
            Sécurité
          </div>
          <h1 className="text-2xl font-bold text-[#0D1B2A]">Changer votre mot de passe</h1>
          <p className="mt-2 text-sm text-slate-500">
            Pour votre sécurité, veuillez définir un nouveau mot de passe personnel.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="current" className="text-[#0D1B2A] font-medium">Mot de passe actuel</Label>
            <Input id="current" data-testid={TID.cpwCurrent} type="password" value={current}
              onChange={(e) => setCurrent(e.target.value)} className="mt-1.5 h-11" required />
          </div>
          <div>
            <Label htmlFor="next" className="text-[#0D1B2A] font-medium">Nouveau mot de passe</Label>
            <Input id="next" data-testid={TID.cpwNew} type="password" value={next}
              onChange={(e) => setNext(e.target.value)} className="mt-1.5 h-11" minLength={10} required />
            {next.length > 0 && (
              <>
                <div className="mt-2 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className={`h-full transition-all ${
                      strength <= 2 ? 'bg-red-500' : strength <= 4 ? 'bg-amber-500' : 'bg-emerald-500'
                    }`}
                    style={{ width: `${(strength / 5) * 100}%` }}
                  />
                </div>
                <div className="mt-2 grid grid-cols-2 gap-1">
                  <Rule ok={rules.length} text="≥ 10 caractères" />
                  <Rule ok={rules.upper} text="Une majuscule" />
                  <Rule ok={rules.lower} text="Une minuscule" />
                  <Rule ok={rules.digit} text="Un chiffre" />
                  <Rule ok={rules.symbol} text="Un caractère spécial" />
                </div>
              </>
            )}
          </div>
          <div>
            <Label htmlFor="confirm" className="text-[#0D1B2A] font-medium">Confirmer</Label>
            <Input id="confirm" type="password" value={confirm}
              onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 h-11" required />
          </div>

          <Button data-testid={TID.cpwSubmit} disabled={loading || !allValid || next !== confirm} className="w-full h-11 bg-[#1677FF] hover:bg-[#0E62DB]">
            {loading ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </form>
        <button onClick={() => setLogoutOpen(true)} className="mt-6 text-xs text-slate-500 hover:text-[#0D1B2A]">Me déconnecter</button>
      </div>
      <LogoutDialog open={logoutOpen} onOpenChange={setLogoutOpen} />
    </div>
  );
}
